// A topic's list: what is to be settled in it and what has been.
//
// The topic cards and the add-topic card, shown by `DecisionsPage`.
//
// The objective is not a decision and does not sit in a topic's list; it is on the home page.
//
// The people are not here any more: they are the family page (`FamilyPage.tsx`).
//
// Non-asset decisions sit in the same list as assets, undistinguished, because "they are
// often the currency that settles the asset fights" and a section header saying otherwise
// would be this interface deciding they are a lesser category. Read the header of
// `DecisionCard` for what a row shows. A principal invited into the topic can add, reword
// and remove issues here; what an issue says is worked on in its own page.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { Decision, RecordChange, TopicView } from '../api/contract.js';
import { changedLine } from '../format.js';
import type { Internals } from '../internals.js';
import { changedClass, useChangedIds } from '../plan/useChangedIds.js';
import { viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import {
  FormButtons,
  InlineForm,
  RemoveConfirm,
  RowMenu,
  cancelOnEscape,
  focusRowMenu,
} from '../components/RowEdit.js';

export type DecisionsProps = {
  state: PlanState;
  internals: Internals;
  client: ApiClient;
  planId: string;
  /** The topic whose list this is. */
  topic: TopicView;
  /** Shows the list without the means to change it, even for a principal. */
  readOnly?: boolean;
  /** The words on the dashed row that adds an entry. */
  addLabel?: string;
};

export function Decisions({
  state,
  internals,
  client,
  planId,
  topic,
  readOnly = false,
  addLabel = 'Add',
}: DecisionsProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const navigate = useNavigate();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  // The one open form or confirmation; opening another replaces it.
  const [open, setOpen] = useState<
    | { readonly kind: 'add' }
    | { readonly kind: 'rename' | 'remove'; readonly id: string }
    | null
  >(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The issue the viewer last acted in comes first; the rest keep their order (the sort is stable).
  const decisions = (snapshot?.decisions ?? [])
    .filter((decision) => decision.topic === topic.key)
    .sort((a, b) => (b.lastActedAt ?? '').localeCompare(a.lastActedAt ?? ''));
  const changed = useChangedIds(decisions, (d) =>
    JSON.stringify([d.label, d.intention, d.position, d.openQuestion]),
  );
  if (snapshot === null) {
    return null;
  }
  const { DecisionCard } = internals;

  const canEdit = !readOnly && viewerIsPrincipal(snapshot) && topic.viewer.invited;

  /** Sends one change. Resolves true when it was accepted. */
  const send = async (change: RecordChange): Promise<boolean> => {
    setBusy(true);
    setProblem(null);
    try {
      await client.changeRecord(planId, change);
      state.refetch();
      return true;
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t save. Try again.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  const menuItems = (decision: Decision): { label: string; run: () => void }[] => {
    const items = [
      {
        label: 'Rename',
        run: () => {
          setProblem(null);
          setOpen({ kind: 'rename', id: decision.id });
        },
      },
    ];
    items.push({
      label: 'Remove',
      run: () => {
        setProblem(null);
        setOpen({ kind: 'remove', id: decision.id });
      },
    });
    return items;
  };

  const rename = open?.kind === 'rename' ? open : null;

  return (
    <div className="decisionsPage">
      {decisions.length === 0 ? (
        <p className="decisions__empty">There is nothing on the list yet.</p>
      ) : (
        <ul className="people__list">
          {decisions.map((decision) => (
            <DecisionCard
              key={decision.id}
              decision={decision}
              highlightClass={changedClass(changed, decision.id)}
              changedNote={changedLine(
                decision.changedBy,
                decision.changedAt,
                decision.createdAt,
                snapshot.people,
              )}
              editor={
                rename !== null && rename.id === decision.id ? (
                  <InlineForm
                    label="Name"
                    initial={decision.label}
                    busy={busy}
                    error={problem}
                    onCancel={() => {
                      setProblem(null);
                      setOpen(null);
                    }}
                    onSave={async (value) => {
                      const saved = await send({
                        kind: 'rename_decision',
                        decision: decision.id,
                        label: value,
                      });
                      if (saved) {
                        setOpen(null);
                      }
                    }}
                  />
                ) : (
                  <Link
                    className="person__name person__name--link"
                    to={`/plans/${planId}/topics/${topic.key}/issues/${decision.id}`}
                  >
                    {decision.label}
                  </Link>
                )
              }
              actions={
                canEdit ? (
                  <RowMenu
                    open={menuFor === decision.id}
                    onToggle={() =>
                      setMenuFor(menuFor === decision.id ? null : decision.id)
                    }
                    items={menuItems(decision)}
                    onPick={() => setMenuFor(null)}
                    name={decision.label}
                    rowId={decision.id}
                  />
                ) : undefined
              }
            >
              {open?.kind === 'remove' && open.id === decision.id ? (
                <RemoveConfirm
                  question={`Remove "${decision.label}" from the list?`}
                  busy={busy}
                  error={problem}
                  onCancel={() => {
                    setProblem(null);
                    setOpen(null);
                    focusRowMenu(decision.id);
                  }}
                  onRemove={() => {
                    void send({ kind: 'remove_decision', decision: decision.id }).then(
                      (removed) => {
                        if (removed) {
                          setOpen(null);
                        }
                      },
                    );
                  }}
                />
              ) : null}
            </DecisionCard>
          ))}
        </ul>
      )}
      {canEdit ? (
        open?.kind === 'add' ? (
          <AddTopicForm
            busy={busy}
            error={problem}
            onCancel={() => {
              setProblem(null);
              setOpen(null);
            }}
            onSave={async (value) => {
              setBusy(true);
              setProblem(null);
              try {
                const id = await client.addIssue(planId, topic.key, value);
                setOpen(null);
                navigate(`/plans/${planId}/topics/${topic.key}/issues/${id}`);
              } catch (error) {
                setProblem(
                  error instanceof ApiFailure && error.code === 'refused'
                    ? error.message
                    : 'That didn’t save. Try again.',
                );
              } finally {
                setBusy(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="addPerson"
            onClick={() => {
              setProblem(null);
              setOpen({ kind: 'add' });
            }}
          >
            <span className="addPerson__plus" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M7 2v10" />
                <path d="M2 7h10" />
              </svg>
            </span>
            {addLabel}
          </button>
        )
      ) : null}
      {problem === null || open !== null ? null : (
        <p className="page__problem" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}

function AddTopicForm({
  busy,
  error,
  onCancel,
  onSave,
}: {
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (value: string) => Promise<void>;
}): JSX.Element {
  const [value, setValue] = useState('');
  const typed = value.trim() !== '';
  return (
    <div className="personAdd">
      <div className="person person--adding person--addingOne">
        <span className="person__avatar person__avatar--empty" aria-hidden="true" />
        <form
          className="inlineForm inlineForm--add"
          onKeyDown={cancelOnEscape(onCancel)}
          onSubmit={(event) => {
            event.preventDefault();
            if (typed && !busy) {
              void onSave(value.trim());
            }
          }}
        >
          <input
            className="inlineForm__input"
            aria-label="Name"
            placeholder="Name"
            value={value}
            autoFocus
            onChange={(event) => setValue(event.target.value)}
          />
          <FormButtons submitLabel="Add" disabled={busy || !typed} onCancel={onCancel} />
          {error === null ? null : <p className="inlineForm__error">{error}</p>}
        </form>
      </div>
    </div>
  );
}
